import { TextInput } from 'cpool-components/input/TextInput';
import {
    ALPHA_2_ISO_3166,
    ALPHA_2_ISO_3166_EU,
    LIMITED_RESIDENCE_PERMIT_TYPES,
    PERSONAL_STATUS,
    RESIDENCE_PERMIT_TYPES,
    SALUTATIONS,
} from 'cpool-constants';
import type { ApplicantCreation } from 'cpool-schemas';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormDateInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDateInput';
import { FormDropdown } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDropdown';
import { formatFormLabel } from '~/features/leadDetails/components/Overview/CustomerData/shared/formatFormLabel';
import { FormCard } from '~/features/leadDetails/components/Overview/CustomerData/shared/FormCard';
import { getFormErrorMessage } from '~/features/leadDetails/components/Overview/CustomerData/shared/getFormErrorMessage';

export const PersonalData: React.FC = () => {
    const { t } = useTranslation();

    const {
        control,
        formState: { errors },
        register,
    } = useFormContext<ApplicantCreation>();

    const countryOptions = Object.values(ALPHA_2_ISO_3166).map((value) => ({
        label: t(`countries.${value}`),
        value,
    }));

    const salutationOptions = Object.values(SALUTATIONS).map((value) => ({
        label: t(`SALUTATIONS.${value}`),
        value,
    }));

    const personalStatusOptions = Object.values(PERSONAL_STATUS).map((value) => ({
        label: t(`maritalStatus.${value}`),
        value,
    }));

    const residencePermitOptions = Object.values(RESIDENCE_PERMIT_TYPES).map((value) => ({
        label: t(`RESIDENCE_PERMIT_TYPES.${value}`),
        value,
    }));

    const staatsangehoerigkeit = useWatch({ control, name: 'personendaten.staatsangehoerigkeit' });
    const aufenthaltstitel = useWatch({ control, name: 'personendaten.aufenthaltstitel' });

    const isNotGerman = staatsangehoerigkeit && staatsangehoerigkeit !== ALPHA_2_ISO_3166.DE;
    const isNotEU =
        staatsangehoerigkeit &&
        !(Object.values(ALPHA_2_ISO_3166_EU) as string[]).includes(staatsangehoerigkeit as string);
    const showResidencePermitExpiry =
        aufenthaltstitel &&
        (
            [
                ...LIMITED_RESIDENCE_PERMIT_TYPES,
                RESIDENCE_PERMIT_TYPES.RESIDENCE_PERMIT_SWITZERLAND,
            ] as unknown as string[]
        ).includes(aufenthaltstitel as string);

    return (
        <div className="flex w-full flex-col gap-2">
            <FormCard>
                <FormDropdown
                    id="personendaten.anrede"
                    label={t('PERSONAL_DATA.SALUTATIONS')}
                    searchable={false}
                    required
                    options={salutationOptions}
                    control={control}
                />

                <TextInput
                    id="personendaten.vorname"
                    label={formatFormLabel(t('PERSONAL_DATA.FIRSTNAME'), true)}
                    labelClassName="w-[175px] justify-start"
                    error={getFormErrorMessage(errors, 'personendaten.vorname')}
                    {...register('personendaten.vorname')}
                />

                <TextInput
                    id="personendaten.nachname"
                    label={formatFormLabel(t('PERSONAL_DATA.LASTNAME'), true)}
                    labelClassName="w-[175px] justify-start"
                    error={getFormErrorMessage(errors, 'personendaten.nachname')}
                    {...register('personendaten.nachname')}
                />

                <TextInput
                    id="personendaten.geburtsname"
                    label={t('PERSONAL_DATA.BIRTH_NAME')}
                    labelClassName="w-[175px] justify-start"
                    error={getFormErrorMessage(errors, 'personendaten.geburtsname')}
                    {...register('personendaten.geburtsname')}
                />

                <FormDateInput
                    id="personendaten.geburtsdatum"
                    label={t('PERSONAL_DATA.BIRTHDATE')}
                    required
                    control={control}
                />

                <TextInput
                    id="personendaten.geburtsort"
                    label={t('PERSONAL_DATA.BIRTHPLACE')}
                    labelClassName="w-[175px] justify-start"
                    error={getFormErrorMessage(errors, 'personendaten.geburtsort')}
                    {...register('personendaten.geburtsort')}
                />

                <FormDropdown
                    id="personendaten.geburtsland"
                    label={t('PERSONAL_DATA.COUNTRY_OF_BIRTH')}
                    options={countryOptions}
                    control={control}
                />

                <FormDropdown
                    id="personendaten.staatsangehoerigkeit"
                    label={t('PERSONAL_DATA.NATIONALITY')}
                    required
                    options={countryOptions}
                    control={control}
                />

                {isNotGerman && (
                    <FormDateInput
                        id="personendaten.inDeutschlandSeit"
                        label={t('PERSONAL_DATA.IN_GERMANY_SINCE')}
                        required
                        control={control}
                    />
                )}

                {isNotEU && (
                    <FormDropdown
                        id="personendaten.aufenthaltstitel"
                        label={t('PERSONAL_DATA.RESIDENCE_PERMIT')}
                        required
                        options={residencePermitOptions}
                        control={control}
                    />
                )}

                {showResidencePermitExpiry && (
                    <FormDateInput
                        id="personendaten.aufenthaltBefristetBis"
                        label={t('PERSONAL_DATA.RESIDENCE_PERMIT_UNTIL')}
                        required
                        control={control}
                    />
                )}

                <FormDropdown
                    id="personendaten.familienstand"
                    label={t('PERSONAL_DATA.MARITAL_STATUS')}
                    options={personalStatusOptions}
                    control={control}
                />

                <TextInput
                    id="personendaten.email"
                    label={formatFormLabel(t('PERSONAL_DATA.MAIL'), true)}
                    labelClassName="w-[175px] justify-start"
                    error={getFormErrorMessage(errors, 'personendaten.email')}
                    {...register('personendaten.email')}
                />

                <TextInput
                    id="personendaten.telefonPrivat"
                    label={formatFormLabel(t('PERSONAL_DATA.PHONE'), true)}
                    labelClassName="w-[175px] justify-start"
                    error={getFormErrorMessage(errors, 'personendaten.telefonPrivat')}
                    {...register('personendaten.telefonPrivat')}
                />
            </FormCard>
        </div>
    );
};
